import { Model } from 'miragejs'
import type { ModelDefinition } from 'miragejs/-types'
import type { MirageServer, MockServer } from '@/api/mock-server/server.js'
import { createErrorResponse } from '@/api/mock-server/utils.js'
import { UserFilterKeys } from '@/models/userFilter.js'
import type { SchemaAttributes } from '@/api/types.js'

type UserFilterData = SchemaAttributes<'UserFilter'>

export const model = Model as ModelDefinition<UserFilterData>

/**
 * Store the user filters in localStorage.
 *
 * Since Mirage's in-memory database is lost on page refresh, we store filter data
 * in localStorage to persist across browser reloads during development.
 * This way we can replicate the backend behavior locally.
 *
 * Storage structure: `pvc-mirage-user-filters:{userId}:{departmentId}:{filterKey}`
 * - Scoped per user and department (matches backend behavior)
 * - Each filter key (e.g., 'invoice-items-columns') stores its own data
 * - Data is JSON serialized
 */
const STORAGE_KEY_PREFIX = 'pvc-mirage-user-filters:'

const getUserFilterKey = (
  userId: string,
  departmentId: string,
  filterKey: string,
): string => {
  return `${STORAGE_KEY_PREFIX}${userId}:${departmentId}:${filterKey}`
}

const getFilterFromStorage = (storageKey: string): Record<string, unknown> => {
  if (typeof window === 'undefined') {
    return {}
  }

  const stored = localStorage.getItem(storageKey)
  if (!stored) {
    return {}
  }

  try {
    return JSON.parse(stored)
  } catch {
    return {}
  }
}

const setFilterToStorage = (
  storageKey: string,
  data: Record<string, unknown>,
): void => {
  if (typeof window === 'undefined') {
    return
  }

  localStorage.setItem(storageKey, JSON.stringify(data))
}

export function routes(server: MockServer) {
  server.get(
    '/users/filters/:filter_key',
    function (this: MirageServer, schema, request) {
      const filterKey = request.params.filter_key
      const user = schema.first('user')

      if (!user) {
        return createErrorResponse({
          status: 403,
          errors: [{ detail: 'User not authenticated' }],
        })
      }

      const activeDepartment = user.activeDepartment
      if (!activeDepartment || !user.id) {
        return createErrorResponse({
          status: 400,
          errors: [{ detail: 'User has no active department' }],
        })
      }

      const storageKey = getUserFilterKey(
        user.id.toString(),
        activeDepartment.id.toString(),
        filterKey,
      )

      const data = getFilterFromStorage(storageKey)

      return {
        data: {
          type: 'UserFilter',
          id: filterKey,
          attributes: {
            filter_key: filterKey,
            data,
          },
        },
      }
    },
  )

  server.patch(
    '/users/filters/:filter_key',
    function (this: MirageServer, schema, request) {
      const filterKey = request.params.filter_key
      const user = schema.first('user')

      if (!user) {
        return createErrorResponse({
          status: 403,
          errors: [{ detail: 'User not authenticated' }],
        })
      }

      const activeDepartment = user.activeDepartment
      if (!activeDepartment || !user.id) {
        return createErrorResponse({
          status: 400,
          errors: [{ detail: 'User has no active department' }],
        })
      }

      const body = JSON.parse(request.requestBody)
      const newData = body?.data?.attributes?.data || {}

      const storageKey = getUserFilterKey(
        user.id.toString(),
        activeDepartment.id.toString(),
        filterKey,
      )

      setFilterToStorage(storageKey, newData)

      return {
        data: {
          type: 'UserFilter',
          id: filterKey,
          attributes: {
            filter_key: filterKey,
            data: newData,
          },
        },
      }
    },
  )
}

export function seeds(server: MockServer) {
  const user = server.schema.first('user')
  const department = user?.activeDepartment

  if (!user?.id || !department?.id) {
    return
  }

  // Seed some example filters for development/testing
  const userId = user.id.toString()
  const departmentId = department.id.toString()

  // Table column preferences
  setFilterToStorage(
    getUserFilterKey(
      userId,
      departmentId,
      UserFilterKeys.InvoiceFinalizedItemsColumns,
    ),
    {
      column_visibility: {},
      column_order: [],
    },
  )

  setFilterToStorage(
    getUserFilterKey(
      userId,
      departmentId,
      UserFilterKeys.InvoiceFinalizedPaymentsColumns,
    ),
    {
      column_visibility: {},
      column_order: [],
    },
  )

  setFilterToStorage(
    getUserFilterKey(
      userId,
      departmentId,
      UserFilterKeys.InvoiceFinalizedCreditNotesColumns,
    ),
    {
      column_visibility: {},
      column_order: [],
    },
  )

  setFilterToStorage(
    getUserFilterKey(
      userId,
      departmentId,
      UserFilterKeys.PlaygroundManageColumns,
    ),
    {
      column_visibility: {},
      column_order: [],
    },
  )
}
